import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { LOGO_BASE64 } from './logoData';

const G = rgb(0.102, 0.478, 0.29);   // #1a7a4a green
const N = rgb(0.102, 0.227, 0.361);  // #1a3a5c navy
const K = rgb(0, 0, 0);              // black
const W = rgb(1, 1, 1);              // white
const LG = rgb(0.95, 0.98, 0.95);    // light green bg
const GR = rgb(0.7, 0.7, 0.7);       // gray
const GR_LIGHT = rgb(0.9, 0.9, 0.9); // light gray for zebra stripes

const PW = 595.28;
const PH = 841.89;
const M = 40; // larger margin for cleaner look

function safeText(str) {
    if (!str) return '';
    let val = String(str);
    val = val.replace(/[\n\r\t]/g, ' ');
    return val.replace(/[^\x20-\x7E\xA0-\xFF]/g, '');
}

async function fetchLogoImage(doc) {
    try {
        const binaryStr = atob(LOGO_BASE64);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
        return await doc.embedJpg(bytes.buffer);
    } catch {
        return null;
    }
}

export async function generateStatsPdf(monthName, monthData, categorias) {
    const doc = await PDFDocument.create();
    const f = await doc.embedFont(StandardFonts.Helvetica);
    const fb = await doc.embedFont(StandardFonts.HelveticaBold);

    const logo = await fetchLogoImage(doc);

    // Add the first page
    let page = doc.addPage([PW, PH]);
    let hy = PH - M;

    // Header Zone
    if (logo) {
        page.drawImage(logo, { x: M, y: hy - 50, width: 50, height: 50 });
    }

    page.drawText('CUERPO DE RESCATE DE ENSENADA, A.C.', { x: M + 65, y: hy - 14, size: 12, font: fb, color: N });
    page.drawText('REPORTE DE ESTADÍSTICAS', { x: M + 65, y: hy - 30, size: 10, font: fb, color: G });
    page.drawText(`Mes: ${safeText(monthName)}`, { x: M + 65, y: hy - 45, size: 9, font: f, color: K });

    // Date of generation
    const today = new Date().toLocaleDateString('es-MX');
    page.drawText(`Generado: ${today}`, { x: PW - M - 90, y: hy - 14, size: 8, font: f, color: GR });

    page.drawLine({ start: { x: M, y: hy - 60 }, end: { x: PW - M, y: hy - 60 }, color: G, thickness: 1.5 });

    hy -= 80;

    // Total Summary
    page.drawRectangle({ x: M, y: hy - 25, width: PW - 2 * M, height: 25, color: G });
    page.drawText(`TRABAJOS DEL MES: ${monthData.total}`, { x: M + 15, y: hy - 17, size: 10, font: fb, color: W });

    hy -= 40;

    // Table Headers
    page.drawText('CATEGORÍA', { x: M + 15, y: hy - 12, size: 8, font: fb, color: N });
    page.drawText('CANTIDAD', { x: PW - M - 70, y: hy - 12, size: 8, font: fb, color: N });
    page.drawLine({ start: { x: M, y: hy - 18 }, end: { x: PW - M, y: hy - 18 }, color: N, thickness: 1 });

    hy -= 18;

    // Table Data
    let isEven = false;

    for (const cat of categorias) {
        const val = monthData.list[cat] || 0;

        // Add new page if space runs out
        if (hy < M + 20) {
            page = doc.addPage([PW, PH]);
            hy = PH - M;

            // Reprint Header on new pages
            page.drawText('CATEGORÍA', { x: M + 15, y: hy - 12, size: 8, font: fb, color: N });
            page.drawText('CANTIDAD', { x: PW - M - 70, y: hy - 12, size: 8, font: fb, color: N });
            page.drawLine({ start: { x: M, y: hy - 18 }, end: { x: PW - M, y: hy - 18 }, color: N, thickness: 1 });
            hy -= 18;
        }

        const rowH = 16;

        // Zebra background
        if (isEven) {
            page.drawRectangle({ x: M, y: hy - rowH, width: PW - 2 * M, height: rowH, color: LG });
        }

        // Text
        const catTextSafe = safeText(cat);
        const textIsBold = val > 0;

        page.drawText(catTextSafe, { x: M + 15, y: hy - 11, size: 8, font: textIsBold ? fb : f, color: textIsBold ? N : GR });
        page.drawText(String(val), { x: PW - M - 50, y: hy - 11, size: 8, font: textIsBold ? fb : f, color: textIsBold ? G : GR });

        hy -= rowH;
        isEven = !isEven;
    }

    // Bottom Line
    page.drawLine({ start: { x: M, y: hy }, end: { x: PW - M, y: hy }, color: GR, thickness: 1 });

    // Save the PDF
    const pdfBytes = await doc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const objectUrl = URL.createObjectURL(blob);

    // Trigger download
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `Estadisticas_FRAP_${safeText(monthName.replace(/\s+/g, '_'))}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
}
